/**
 * O contador de aniversário do painel, o mesmo do Flutter.
 *
 * É a transcrição de `anniversary_metrics.dart` e `metric_card.dart`: a
 * sobrancelha dourada em cima, seis cartões (anos, meses, dias, horas, minutos
 * e segundos, o último pulsando) e a pílula de "N dias de nós" embaixo.
 *
 * Fica fora do `Painel` porque anda de segundo em segundo. Lá dentro, cada
 * batida redesenharia a tela inteira — humores, marcos, atalhos — para mudar
 * dois dígitos. Aqui o `useState` do relógio só alcança estes seis cartões,
 * que é o mesmo cuidado que o `ValueNotifier` toma do lado do Flutter.
 */

import { useEffect, useState } from 'react';
import { comecoCurto, juntosDesde, locale, traduzir, type Juntos } from '@nosso-sempre/shared';
import { Icone } from './Icone';
import './contador.css';

interface Props {
  inicio: Date;
}

export function ContadorJuntos({ inicio }: Props) {
  const [agora, setAgora] = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setAgora(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const j = juntosDesde(inicio, agora);
  /* Maiúscula aqui e não no dicionário: o `.selo` e o `.quanto` não têm
     `text-transform`, mas as palavras são as mesmas das medidas, que têm. */
  const etiqueta = (
    j.anos === 0
      ? `${j.meses} ${traduzir(j.meses === 1 ? 'mês' : 'meses')}`
      : `${j.anos} ${traduzir(j.anos === 1 ? 'ano' : 'anos')}`
  ).toUpperCase();

  return (
    <section className="contador" aria-label={traduzir('Tempo juntos')}>
      <header className="sobrancelha">
        <span className="fio" aria-hidden="true" />
        <span className="selo">
          <Icone nome="coracao" tamanho={11} preenchido />
          {traduzir('Tempo juntos').toUpperCase()}
        </span>
        <span className="fio virado" aria-hidden="true" />
        <span className="quanto">{etiqueta}</span>
      </header>

      <div className="medidas">
        {(
          [
            ['anos', j.anos],
            ['meses', j.meses],
            ['dias', j.dias],
            ['horas', j.horas],
            ['minutos', j.minutos],
            ['segundos', j.segundos],
          ] as ReadonlyArray<[string, number]>
        ).map(([nome, valor], i) => (
          <Medida key={nome} nome={nome} valor={valor} pulsa={i === 5} />
        ))}
      </div>

      <p className="desde">
        <span className="pontinho" aria-hidden="true" />
        {traduzir('{dias} dias de nós · desde {data}', {
          dias: j.totalDias.toLocaleString(locale()),
          data: comecoCurto(inicio),
        })}
        <Icone nome="coracao" tamanho={12} preenchido />
      </p>
    </section>
  );
}

function Medida({ nome, valor, pulsa }: { nome: string; valor: number; pulsa: boolean }) {
  const dois = String(valor).padStart(2, '0');
  return (
    <div className={pulsa ? 'medida pulsa' : 'medida'}>
      {/* O mesmo número, grande e quase invisível, atrás do de verdade. */}
      <span className="fantasma" aria-hidden="true">
        {dois}
      </span>
      <span className="valor">
        {pulsa ? <span className="pontinho" aria-hidden="true" /> : null}
        {dois}
      </span>
      <span className="risco" aria-hidden="true" />
      <span className="nome">{traduzir(nome)}</span>
    </div>
  );
}

/** Para o painel poder mostrar a mesma conta em palavras, sem recontar. */
export type { Juntos };
